/**
 * GiftGiverGridItem
 *
 * A single gifter cell in the content "gifts received" grid. Ports Flutter's two
 * distinct tile variants from `grid_widget.dart`, selected on `transactionType`
 * (the web equivalent of `media.type = unlockContent ? 5 : 4`,
 * content_gifts_bottom_sheet.dart:106-109):
 *
 *   - gift          (AppTransactionType.gift, 1)          → `_giftListItem`
 *     (grid_widget.dart:508-575): avatar + gift image (`GiftUtils.getGiftPath`) + coins.
 *   - unlockContent (AppTransactionType.unlockContent, 3) → `_unlockContentListItem`
 *     (grid_widget.dart:577-630): avatar + a static red-tinted `unlocked.png`
 *     icon (NEVER a gift image) + coins.
 *
 * Shared fields (avatar, name, coins) render identically across both variants.
 * Tapping the tile navigates to the GIFTER's user-details
 * (content_gifts_bottom_sheet.dart:82-85), via the parent-supplied `onTap`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserUtils } from '@/domain/utils/userUtils';
import { MathUtils } from '@/domain/utils/mathUtils';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';

export interface GiftGiverGridItemProps {
  transaction: AppTransactionEntity;
  onTap: (userId: string) => void;
}

export const GiftGiverGridItem: React.FC<GiftGiverGridItemProps> = ({
  transaction,
  onTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const { usersInfo, giftInfo } = transaction;
  const isUnlock = transaction.transactionType === AppTransactionType.unlockContent;
  const mediaSlotSize = responsive.icon.small;

  // The unlock tile renders a static red-tinted icon and NEVER a gift image
  // (grid_widget.dart:597-603). The gift tile renders the gift image when its
  // giftInfo is present (grid_widget.dart:528-547).
  const mediaSlot = isUnlock ? (
    <AssetIcon
      path="/assets/images/unlocked.png"
      width={mediaSlotSize}
      height={mediaSlotSize}
      color={theme.activeRed}
    />
  ) : giftInfo ? (
    <CachedImage
      src={GiftUtils.getGiftPath(giftInfo.thumbnail)}
      alt={giftInfo.name}
      style={{
        width: mediaSlotSize,
        height: mediaSlotSize,
        borderRadius: responsive.borderRadius.base,
        objectFit: 'cover',
      }}
    />
  ) : (
    <div style={{ width: mediaSlotSize, height: mediaSlotSize }} />
  );

  return (
    <div
      {...qaAttr({ id: `content-gift-giver-${usersInfo.userId}` })}
      onClick={() => onTap(usersInfo.userId)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: responsive.gap.xs,
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(usersInfo.userId)}
        alt={usersInfo.name}
        style={{
          width: responsive.avatar.large,
          height: responsive.avatar.large,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
        }}
      />

      {mediaSlot}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.gap.xs,
        }}
      >
        <span
          style={{
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {MathUtils.roundDoubleTwoDecimals(giftInfo?.coins)}
        </span>
        <AssetIcon
          path="/assets/images/coins.png"
          width={responsive.icon.xs}
          height={responsive.icon.xs}
          color={theme.exclusive}
        />
      </div>
    </div>
  );
};
